import { useCallback, useState } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { AdminSession } from '../../types'
import { toSession } from './securityMappers'
import {
  requestRevokeSecuritySession,
  requestSecuritySessions,
  requestTrustSecuritySession,
} from './securitySessionsApi'
import { mapSecuritySessions, mergeSecuritySession } from './useSecuritySessions.helpers'

type Translator = (key: string, params?: Record<string, string | number>) => string

type Args = {
  token: string | null
  authHeaders: (withJson?: boolean) => Record<string, string>
  t: Translator
}

export const useSecuritySessions = ({ token, authHeaders, t }: Args) => {
  const [sessions, setSessions] = useState<AdminSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)

  const loadSessions = useCallback(async () => {
    if (!token) return
    setSessionsLoading(true)
    try {
      const sessionsPayload = await requestSecuritySessions({ authHeaders, t })
      setSessions(mapSecuritySessions(t, sessionsPayload))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('security.sessions.loadError'))
    } finally {
      setSessionsLoading(false)
    }
  }, [authHeaders, t, token])

  const bootstrapSession = useCallback(
    (payload?: unknown) => {
      if (!payload) return
      const session = toSession(t, payload)
      if (!session.id) return
      setSessions((prev) => mergeSecuritySession(prev, session))
    },
    [t],
  )

  const trustSession = useCallback(
    async (id: string) => {
      if (!token) {
        toast.error(t('security.sessions.loginRequired'))
        return
      }
      try {
        const data = await requestTrustSecuritySession({
          authHeaders,
          sessionId: id,
          t,
        })
        const session = toSession(t, data.session)
        setSessions((prev) => prev.map((item) => (item.id === session.id ? session : item)))
        toast.success(t('security.sessions.trusted'))
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('security.sessions.updateError'))
      }
    },
    [authHeaders, t, token],
  )

  const revokeSession = useCallback(
    async (id: string) => {
      if (!token) {
        toast.error(t('security.sessions.loginRequired'))
        return { isCurrent: false }
      }
      try {
        const result = await requestRevokeSecuritySession({
          authHeaders,
          sessionId: id,
          t,
        })
        setSessions((prev) => prev.filter((item) => item.id !== id))
        toast.success(t('security.sessions.revoked'))
        return result
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('security.sessions.revokeError'))
        return { isCurrent: false }
      }
    },
    [authHeaders, t, token],
  )

  const resetSessions = useCallback(() => {
    setSessions([])
  }, [])

  return {
    sessions,
    sessionsLoading,
    loadSessions,
    trustSession,
    revokeSession,
    bootstrapSession,
    resetSessions,
  }
}
